import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  X,
  Search,
  ChevronDown,
  ChevronUp,
  Users,
  Shield,
  Clock,
  Calendar,
  Ban,
  AlertTriangle,
  ArrowUpDown,
  Check,
} from 'lucide-react';
import DatePickerField from '../UI/DatePickerField';
import toast from '../../utils/toast';
import { staffAPI } from '../../services/api';
import Portal from '../UI/Portal';
import { useIsMobile } from '../../hooks/useIsMobile';
import { toLocalISODate, parseLocalDate } from '../../utils/dateUtils';
import { TradeBadge } from '../UI/TradeBadge';

const WORK_HOURS = [];
for (let h = 6; h <= 23; h++) WORK_HOURS.push(`${String(h).padStart(2, '0')}:00`);
for (let h = 0; h <= 5; h++) WORK_HOURS.push(`${String(h).padStart(2, '0')}:00`);

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const PersonnelPicker = ({ tradeGroups, selected, onChange, loading, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedTrades, setExpandedTrades] = useState({});
  const ref = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (isOpen && searchRef.current) searchRef.current.focus();
  }, [isOpen]);

  const toggleTrade = (name) => setExpandedTrades(p => ({ ...p, [name]: p[name] === false ? true : false }));

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return (tradeGroups || [])
      .map(g => ({
        ...g,
        workers: g.workers.filter(w =>
          !q ||
          `${w.first_name} ${w.last_name}`.toLowerCase().includes(q) ||
          w.staff_id.toLowerCase().includes(q)
        ),
      }))
      .filter(g => g.workers.length > 0);
  }, [tradeGroups, search]);

  const allWorkers = useMemo(() => (tradeGroups || []).flatMap(g => g.workers), [tradeGroups]);
  const selectedNames = useMemo(() => {
    const map = {};
    allWorkers.forEach(w => { map[w.staff_id] = `${w.first_name} ${w.last_name}`; });
    return selected.map(id => map[id] || id);
  }, [selected, allWorkers]);

  const label = selected.length === 0
    ? (placeholder || 'Select...')
    : selected.length <= 2
      ? selectedNames.join(', ')
      : `${selected.length} selected`;

  const toggle = (staffId) => {
    onChange(selected.includes(staffId) ? selected.filter(id => id !== staffId) : [...selected, staffId]);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => !loading && setIsOpen(!isOpen)}
        disabled={loading}
        className={`input w-full text-left flex items-center justify-between ${!selected.length ? 'text-gray-400' : 'text-gray-900'}`}
      >
        <span className="truncate">{loading ? 'Loading...' : label}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-80 overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchRef}
                type="text"
                className="input-admin text-sm pl-9 pr-3"
                placeholder="Search by name or staff ID..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-60">
            {filtered.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">No results found</div>
            ) : (
              filtered.map(group => (
                <div key={group.trade_category}>
                  <button
                    type="button"
                    onClick={() => toggleTrade(group.trade_category)}
                    className="w-full px-3 py-2 flex items-center justify-between bg-gray-50 hover:bg-gray-100 text-left"
                  >
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide flex items-center gap-2">
                      <TradeBadge trade={group.trade_category} size="xs" />
                      <span className="normal-case tracking-normal font-medium text-gray-500">
                        ({group.workers.length})
                      </span>
                    </span>
                    {expandedTrades[group.trade_category] !== false
                      ? <ChevronUp className="w-3.5 h-3.5 text-gray-400" />
                      : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                    }
                  </button>
                  {expandedTrades[group.trade_category] !== false && (
                    <div>
                      {group.workers.map(w => {
                        const checked = selected.includes(w.staff_id);
                        return (
                          <label
                            key={w.staff_id}
                            className={`flex items-center gap-2 px-4 pl-8 py-2 hover:bg-blue-50 cursor-pointer text-sm ${checked ? 'bg-blue-50' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggle(w.staff_id)}
                              className="rounded border-gray-300 text-blue-800 focus-visible:ring-blue-500"
                            />
                            <span className="flex-1">{w.first_name} {w.last_name}</span>
                            <span className="text-xs text-gray-400 font-mono">{w.staff_id}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SinglePersonPicker = ({ people, selected, onChange, loading, placeholder, excludeIds = [] }) => {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return (people || [])
      .filter(p => !excludeIds.includes(p.staff_id))
      .filter(p =>
        !q ||
        `${p.first_name} ${p.last_name}`.toLowerCase().includes(q) ||
        p.staff_id.toLowerCase().includes(q)
      );
  }, [people, search, excludeIds]);

  const selectedPerson = useMemo(() => (people || []).find(p => p.staff_id === selected), [people, selected]);
  const label = selectedPerson ? `${selectedPerson.first_name} ${selectedPerson.last_name}` : (placeholder || 'Select...');

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => !loading && setIsOpen(!isOpen)}
        disabled={loading}
        className={`input w-full text-left flex items-center justify-between ${!selected ? 'text-gray-400' : 'text-gray-900'}`}
      >
        <span className="truncate">{loading ? 'Loading...' : label}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                className="input-admin text-sm pl-9 pr-3"
                placeholder="Search..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoFocus={!isMobile}
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-48">
            {filtered.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">No results</div>
            ) : filtered.map(p => (
              <button
                key={p.staff_id}
                type="button"
                onClick={() => { onChange(p.staff_id); setIsOpen(false); }}
                className={`w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-blue-50 text-left ${selected === p.staff_id ? 'bg-blue-50 font-medium' : ''}`}
              >
                {selected === p.staff_id && <Check className="w-3.5 h-3.5 text-blue-700 flex-shrink-0" />}
                <span className="flex-1">{p.first_name} {p.last_name}</span>
                <span className="text-xs text-gray-400 font-mono">{p.staff_id}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Mode components
// ---------------------------------------------------------------------------

const PersonnelMode = ({ request, actionType, onSubmit, isPending }) => {
  const isWorker = actionType.includes('worker');
  const isSwap = actionType.startsWith('swap_');
  const isRemove = actionType.startsWith('remove_');
  const isAdd = actionType.startsWith('add_');
  const role = isWorker ? 'worker' : 'safety';

  const currentAssignments = (request.assignments || []).filter(a => a.role === role && a.is_active);
  const currentIds = currentAssignments.map(a => a.employee_staff_id);

  const rdwDates = request.restday_work_dates || [];
  const minRdwDate = rdwDates.length > 0 ? parseLocalDate(rdwDates[0]) : null;
  const maxRdwDate = rdwDates.length > 0 ? parseLocalDate(rdwDates[rdwDates.length - 1]) : null;

  const [selectedIds, setSelectedIds] = useState([]);
  const [removeId, setRemoveId] = useState('');
  const [addId, setAddId] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [reason, setReason] = useState('');

  const { data: workerGroups, isLoading: workersLoading } = useQuery({
    queryKey: ['restday-work-active-workers'],
    queryFn: () => staffAPI.getRestDayWorkActiveWorkers(),
    select: r => r.data,
    enabled: isWorker,
  });

  const { data: safetyList, isLoading: safetyLoading } = useQuery({
    queryKey: ['restday-work-safety-personnel'],
    queryFn: () => staffAPI.getRestDayWorkSafetyPersonnel(),
    select: r => r.data,
    enabled: !isWorker,
  });

  const safetyGroups = useMemo(() => {
    if (!safetyList) return [];
    return [{ trade_category: 'Safety Department', workers: safetyList }];
  }, [safetyList]);

  const groups = isWorker ? workerGroups : safetyGroups;
  const loading = isWorker ? workersLoading : safetyLoading;
  const allPeople = useMemo(() => (groups || []).flatMap(g => g.workers), [groups]);

  const handleSubmit = () => {
    if (!reason.trim() || reason.trim().length < 5) {
      toast.error('Reason must be at least 5 characters.');
      return;
    }

    let actionPayload = {};

    if (isAdd) {
      if (selectedIds.length === 0) { toast.error(`Select at least one ${role} to add.`); return; }
      actionPayload = isWorker ? { worker_ids: selectedIds } : { safety_ids: selectedIds };
    } else if (isRemove) {
      if (selectedIds.length === 0) { toast.error(`Select at least one ${role} to remove.`); return; }
      actionPayload = isWorker ? { worker_ids: selectedIds } : { safety_ids: selectedIds };
    } else if (isSwap) {
      if (!removeId) { toast.error(`Select the ${role} to remove.`); return; }
      if (!addId) { toast.error(`Select the replacement ${role}.`); return; }
      actionPayload = { remove_id: removeId, add_id: addId };
    }

    if (effectiveDate) {
      actionPayload.effective_date = effectiveDate;
    }

    onSubmit({ action_type: actionType, reason: reason.trim(), payload: actionPayload });
  };

  return (
    <div className="space-y-4">
      {/* Current assignments */}
      <div>
        <p className="text-xs font-medium text-gray-500 mb-2">
          Currently Assigned {isWorker ? 'Workers' : 'Safety Personnel'} ({currentAssignments.length})
        </p>
        <div className="flex flex-wrap gap-2">
          {currentAssignments.map(a => (
            <span key={a.id} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${isWorker ? 'bg-blue-50 text-blue-800' : 'bg-green-50 text-green-800'}`}>
              {isWorker ? <Users className="w-3 h-3" /> : <Shield className="w-3 h-3" />}
              {a.employee_name || a.employee_staff_id}
              <span className="text-gray-400 font-mono">{a.employee_staff_id}</span>
            </span>
          ))}
        </div>
      </div>

      <hr className="border-gray-100" />

      {/* Action-specific form */}
      {isAdd && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Select {isWorker ? 'Workers' : 'Safety Personnel'} to Add
          </label>
          <PersonnelPicker
            tradeGroups={groups || []}
            selected={selectedIds}
            onChange={setSelectedIds}
            loading={loading}
            placeholder={`Select ${role}s to add...`}
          />
        </div>
      )}

      {isRemove && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Select {isWorker ? 'Workers' : 'Safety Personnel'} to Remove
          </label>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {currentAssignments.map(a => {
              const checked = selectedIds.includes(a.employee_staff_id);
              return (
                <label key={a.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer hover:bg-red-50 ${checked ? 'bg-red-50' : 'bg-gray-50'}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => setSelectedIds(prev =>
                      prev.includes(a.employee_staff_id)
                        ? prev.filter(id => id !== a.employee_staff_id)
                        : [...prev, a.employee_staff_id]
                    )}
                    className="rounded border-gray-300 text-red-600 focus-visible:ring-red-500"
                  />
                  <span className="flex-1 text-sm">{a.employee_name || a.employee_staff_id}</span>
                  <span className="text-xs text-gray-400 font-mono">{a.employee_staff_id}</span>
                </label>
              );
            })}
          </div>
          {selectedIds.length >= currentAssignments.length && (
            <p className="text-xs text-red-600 mt-1">Cannot remove all workers. At least one must remain.</p>
          )}
        </div>
      )}

      {isSwap && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Remove</label>
            <div className="space-y-1">
              {currentAssignments.map(a => (
                <label key={a.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer hover:bg-red-50 ${removeId === a.employee_staff_id ? 'bg-red-50 ring-1 ring-red-200' : 'bg-gray-50'}`}>
                  <input
                    type="radio"
                    name="swap_remove"
                    checked={removeId === a.employee_staff_id}
                    onChange={() => setRemoveId(a.employee_staff_id)}
                    className="text-red-600 focus-visible:ring-red-500"
                  />
                  <span className="flex-1 text-sm">{a.employee_name || a.employee_staff_id}</span>
                  <span className="text-xs text-gray-400 font-mono">{a.employee_staff_id}</span>
                </label>
              ))}
            </div>
          </div>
          {removeId && (
            <>
              <div className="flex justify-center">
                <ArrowUpDown className="w-4 h-4 text-gray-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Replace With</label>
                <SinglePersonPicker
                  people={allPeople}
                  selected={addId}
                  onChange={setAddId}
                  loading={loading}
                  placeholder={`Select replacement ${role}...`}
                  excludeIds={currentIds}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* Effective Date */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          {isSwap ? 'Replacement Starts From' : isRemove ? 'Last Working Day' : 'Starts From'}
        </label>
        <DatePickerField
          showIcon={false}
          selected={effectiveDate ? parseLocalDate(effectiveDate) : null}
          onChange={d => setEffectiveDate(d ? toLocalISODate(d) : '')}
          minDate={minRdwDate}
          maxDate={maxRdwDate}
          className="input w-full text-sm"
          placeholderText="Defaults to today if not set"
          isClearable
        />
        <p className="text-xs text-gray-400 mt-1">
          {isSwap
            ? 'First rest day work date for the replacement. Leave blank to default to today.'
            : isRemove
              ? 'Last day this person works. Leave blank to default to today.'
              : 'First rest day work date for the new assignee. Leave blank to default to today.'}
        </p>
      </div>

      {/* Reason */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Reason for Change *</label>
        <textarea
          rows={3}
          className="textarea w-full resize-y text-sm"
          placeholder="Describe the reason for this administrative action..."
          value={reason}
          onChange={e => setReason(e.target.value)}
        />
      </div>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={isPending || reason.trim().length < 5}
        className="btn btn-primary w-full"
      >
        {isPending ? 'Processing...' : 'Confirm Action'}
      </button>
    </div>
  );
};

const ScheduleMode = ({ request, onSubmit, isPending }) => {
  const isRange = request.date_selection_type === 'range';
  const [startDate, setStartDate] = useState(request.start_date || '');
  const [endDate, setEndDate] = useState(request.end_date || '');
  const [specificDates, setSpecificDates] = useState(request.specific_dates || []);
  const [startTime, setStartTime] = useState(request.start_time?.substring(0, 5) || '');
  const [endTime, setEndTime] = useState(request.end_time?.substring(0, 5) || '');
  const [adjustDates, setAdjustDates] = useState(false);
  const [adjustTimes, setAdjustTimes] = useState(false);
  const [reason, setReason] = useState('');

  const handleAddSpecificDate = (dateObj) => {
    if (!dateObj) return;
    const iso = toLocalISODate(dateObj);
    if (!specificDates.includes(iso)) {
      setSpecificDates(prev => [...prev, iso].sort());
    }
  };

  const handleRemoveSpecificDate = (iso) => {
    setSpecificDates(prev => prev.filter(d => d !== iso));
  };

  const hasDateChanges = () => {
    if (isRange) {
      return startDate !== (request.start_date || '') || endDate !== (request.end_date || '');
    }
    const originalDates = request.specific_dates || [];
    return JSON.stringify(specificDates) !== JSON.stringify(originalDates);
  };

  const hasTimeChanges = () => (
    startTime !== (request.start_time?.substring(0, 5) || '') ||
    endTime !== (request.end_time?.substring(0, 5) || '')
  );

  const handleSubmit = () => {
    if (!reason.trim() || reason.trim().length < 5) {
      toast.error('Reason must be at least 5 characters.');
      return;
    }
    if (!adjustDates && !adjustTimes) {
      toast.error('Select at least one adjustment to make.');
      return;
    }

    const actions = [];

    if (adjustDates && hasDateChanges()) {
      const payload = isRange
        ? { start_date: startDate, end_date: endDate }
        : { specific_dates: specificDates };
      actions.push({ action_type: 'adjust_dates', reason: reason.trim(), payload });
    }

    if (adjustTimes && hasTimeChanges()) {
      actions.push({ action_type: 'adjust_times', reason: reason.trim(), payload: { start_time: startTime, end_time: endTime } });
    }

    if (actions.length === 0) {
      toast.error('Change at least one selected date or time value.');
      return;
    }

    onSubmit(actions[0], actions.length > 1 ? actions[1] : null);
  };

  const formatDateShort = (iso) => {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Singapore' });
  };

  return (
    <div className="space-y-4">
      {/* Toggle: Dates */}
      <label className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-50 cursor-pointer hover:bg-gray-100">
        <input
          type="checkbox"
          checked={adjustDates}
          onChange={e => setAdjustDates(e.target.checked)}
          className="rounded border-gray-300 text-blue-800 focus-visible:ring-blue-500"
        />
        <Calendar className="w-4 h-4 text-blue-800" />
        <span className="text-sm font-medium text-gray-900">Adjust Dates</span>
      </label>

      {adjustDates && (
        <div className="pl-4 border-l-2 border-blue-200 space-y-3">
          {isRange ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                <DatePickerField
                  selected={parseLocalDate(startDate)}
                  onChange={d => setStartDate(toLocalISODate(d))}
                  className="input w-full text-sm"
                  placeholderText="Select start..."
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">End Date</label>
                <DatePickerField
                  selected={parseLocalDate(endDate)}
                  onChange={d => setEndDate(toLocalISODate(d))}
                  className="input w-full text-sm"
                  placeholderText="Select end..."
                  minDate={parseLocalDate(startDate)}
                />
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Specific Dates</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {specificDates.map(d => (
                  <span key={d} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-800 rounded-full text-xs">
                    {formatDateShort(d)}
                    <button type="button" onClick={() => handleRemoveSpecificDate(d)} className="hover:text-red-600">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <DatePickerField
                onChange={handleAddSpecificDate}
                className="input w-full text-sm"
                placeholderText="Add a date..."
                isClearable={false}
              />
            </div>
          )}
        </div>
      )}

      {/* Toggle: Times */}
      <label className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-50 cursor-pointer hover:bg-gray-100">
        <input
          type="checkbox"
          checked={adjustTimes}
          onChange={e => setAdjustTimes(e.target.checked)}
          className="rounded border-gray-300 text-blue-800 focus-visible:ring-blue-500"
        />
        <Clock className="w-4 h-4 text-blue-800" />
        <span className="text-sm font-medium text-gray-900">Adjust Times</span>
      </label>

      {adjustTimes && (
        <div className="pl-4 border-l-2 border-blue-200 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Start Time</label>
              <select
                value={startTime}
                onChange={e => setStartTime(e.target.value)}
                className="input w-full text-sm"
              >
                <option value="">Select...</option>
                {WORK_HOURS.map(t => <option key={`s-${t}`} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">End Time</label>
              <select
                value={endTime}
                onChange={e => setEndTime(e.target.value)}
                className="input w-full text-sm"
              >
                <option value="">Select...</option>
                {WORK_HOURS.map(t => <option key={`e-${t}`} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Reason */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Reason for Change *</label>
        <textarea
          rows={3}
          className="textarea w-full resize-y text-sm"
          placeholder="Describe the reason for this schedule adjustment..."
          value={reason}
          onChange={e => setReason(e.target.value)}
        />
      </div>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={isPending || reason.trim().length < 5 || (!adjustDates && !adjustTimes)}
        className="btn btn-primary w-full"
      >
        {isPending ? 'Processing...' : 'Confirm Schedule Change'}
      </button>
    </div>
  );
};

const CancelMode = ({ request, onSubmit, isPending }) => {
  const [reason, setReason] = useState('');

  return (
    <div className="space-y-4">
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-red-900">This action is irreversible</p>
          <p className="text-sm text-red-700 mt-0.5">
            Cancelling will deactivate all worker assignments immediately.
            The Trade Leader and all assigned personnel will be notified.
          </p>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Reason for Cancellation *</label>
        <textarea
          rows={3}
          className="textarea w-full resize-y text-sm"
          placeholder="Explain why HR is cancelling this rest day work request..."
          value={reason}
          onChange={e => setReason(e.target.value)}
        />
      </div>

      <button
        type="button"
        onClick={() => onSubmit({ action_type: 'hr_cancel', reason: reason.trim(), payload: {} })}
        disabled={isPending || reason.trim().length < 5}
        className="btn btn-primary w-full bg-red-600 hover:bg-red-700"
      >
        {isPending ? 'Cancelling...' : 'Cancel Request'}
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Modal
// ---------------------------------------------------------------------------

const ACTION_META = {
  add_workers:    { title: 'Add Workers',              icon: Users,    mode: 'personnel' },
  remove_workers: { title: 'Remove Workers',           icon: Users,    mode: 'personnel' },
  swap_worker:    { title: 'Swap Worker',              icon: Users,    mode: 'personnel' },
  add_safety:     { title: 'Add Safety Personnel',     icon: Shield,   mode: 'personnel' },
  remove_safety:  { title: 'Remove Safety Personnel',  icon: Shield,   mode: 'personnel' },
  swap_safety:    { title: 'Swap Safety Personnel',    icon: Shield,   mode: 'personnel' },
  adjust_schedule:{ title: 'Adjust Schedule',          icon: Calendar, mode: 'schedule' },
  hr_cancel:      { title: 'Cancel Request (HR)',      icon: Ban,      mode: 'cancel' },
};

const RestDayWorkHRActionModal = ({ isOpen, onClose, actionType, request, requestId }) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data) => staffAPI.hrRestDayWorkAdminAction(requestId, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['restday-work'] });
      queryClient.invalidateQueries({ queryKey: ['restday-work-request', requestId] });
      toast.success(res.data?.message || 'HR action completed');
      onClose();
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'HR action failed');
    },
  });

  const handlePersonnelSubmit = (data) => mutation.mutate(data);

  const handleScheduleSubmit = async (first, second) => {
    try {
      await staffAPI.hrRestDayWorkAdminAction(requestId, first);
      if (second) {
        await staffAPI.hrRestDayWorkAdminAction(requestId, second);
      }
      queryClient.invalidateQueries({ queryKey: ['restday-work'] });
      queryClient.invalidateQueries({ queryKey: ['restday-work-request', requestId] });
      toast.success('Schedule updated');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Schedule update failed');
    }
  };

  const handleCancelSubmit = (data) => mutation.mutate(data);

  if (!isOpen || !actionType) return null;

  const meta = ACTION_META[actionType];
  if (!meta) return null;
  const Icon = meta.icon;

  return (
    <Portal>
      <div className="modal-overlay">
        <div className="modal-container modal-container-md">
          {/* Header */}
          <div className="modal-header bg-amber-50 border-b border-amber-200">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4 text-amber-700" />
              </div>
              <div className="min-w-0">
                <h2 className="modal-title">{meta.title}</h2>
                <p className="text-xs text-amber-700 mt-0.5">HR Administrative Action &middot; {request?.request_number}</p>
              </div>
            </div>
            <button onClick={onClose} className="modal-close-admin">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Frozen warning */}
          {request?.status === 'frozen' && (
            <div className="px-6 py-3 bg-amber-50 border-b border-amber-200 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">
                This request is frozen (amendment pending). Proceeding will auto-reject the pending amendment.
              </p>
            </div>
          )}

          {/* Body */}
          <div className="modal-content">
            {meta.mode === 'personnel' && (
              <PersonnelMode
                request={request}
                actionType={actionType}
                onSubmit={handlePersonnelSubmit}
                isPending={mutation.isPending}
              />
            )}
            {meta.mode === 'schedule' && (
              <ScheduleMode
                request={request}
                onSubmit={handleScheduleSubmit}
                isPending={mutation.isPending}
              />
            )}
            {meta.mode === 'cancel' && (
              <CancelMode
                request={request}
                onSubmit={handleCancelSubmit}
                isPending={mutation.isPending}
              />
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default RestDayWorkHRActionModal;
